//React
import React from "react";
import { useEffect, useState } from "react";
//Material UI
import Box from "@mui/material/Box";
//Redux
import { useDispatch } from "react-redux";
import { showInfo, showError } from "../../../Global/notifications/notificationsSlice";
//RTK Query
import {
  useUpdateUserDetailsMutation,
  useGetUserGroupsAvailableQuery,
  useSetUserActiveMutation,
} from "../../../Features/api/apiSlice";
//Components
import UserDialog from "../UserDialog";
import ConfirmDialog from "../ConfirmDialog";
import { DangerButton, PrimaryButton } from "../../../Global/other/Buttons";

/**
 * Row edit action and dialog.
 *
 * The form is seeded from the `user` row rather than a per-user
 * `auth/user_details/` request. `user_list_view` serialises rows with the same
 * `UserSerializerGetUpdate` as the details endpoint, so the row already
 * carries email, first/last name and user_groups — the extra call fetched
 * nothing new and fired once per rendered row.
 */
export default function ButtonEditUser({ user }) {
  const dispatch = useDispatch();
  const { data: available_user_groups, isSuccess: isAvailableGroupsSuccess } =
    useGetUserGroupsAvailableQuery();
  const [updateUser] = useUpdateUserDetailsMutation();
  const [setUserActive] = useSetUserActiveMutation();

  const [values, setValues] = useState({ email: "", first_name: "", last_name: "" });
  const [selected_user_groups, setSelectedUserGroups] = useState([]);
  const [open_edit_user_dialog, setOpenEditUserDialog] = useState(false);
  const [open_deactivate_dialog, setOpenDeactivateDialog] = useState(false);

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();

  // Reseed whenever the dialog opens, so an abandoned edit does not persist
  // into the next one and a refreshed row is picked up.
  useEffect(() => {
    if (!open_edit_user_dialog) return;
    setValues({
      email: user.email || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
    });
    setSelectedUserGroups((user.user_groups || []).map((group) => group.unique_name));
  }, [open_edit_user_dialog, user]);

  const handleChange = (name, value) => setValues((current) => ({ ...current, [name]: value }));

  const handleToggleGroup = (unique_name, checked) => {
    setSelectedUserGroups((current) =>
      checked ? [...current, unique_name] : current.filter((name) => name !== unique_name)
    );
  };

  const handleOnSubmit = (event) => {
    event.preventDefault();
    updateUser({ body: { ...values, user_groups: selected_user_groups, user_id: user.id } });
    setOpenEditUserDialog(false);
  };

  // Archive rather than delete: the record stays linked to install history and
  // can be brought back from the Inactive view. Hard delete now lives only on
  // inactive rows, for accounts created by mistake.
  const handleDeactivate = async () => {
    setOpenDeactivateDialog(false);
    setOpenEditUserDialog(false);
    try {
      await setUserActive({ body: { user_id: user.id, is_active: false } }).unwrap();
      dispatch(showInfo("User deactivated"));
    } catch (e) {
      dispatch(showError(`Could not deactivate ${fullName || "this user"}`));
    }
  };

  if (!isAvailableGroupsSuccess) return <></>;

  return (
    <>
      {/* Was a bare purple EditIcon with no button wrapper, no hover state
          and no accessible name. Now the design's `.icon-link`. */}
      <Box
        component="button"
        type="button"
        onClick={() => setOpenEditUserDialog(true)}
        aria-label={fullName ? `Edit ${fullName}` : "Edit user"}
        sx={{
          width: 30,
          height: 30,
          borderRadius: "6px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255, 255, 255, 0.5)",
          cursor: "pointer",
          border: "none",
          background: "transparent",
          "&:hover": { color: "var(--accent)", background: "rgba(253, 185, 64, 0.10)" },
          "& svg": { width: 15, height: 15 },
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </Box>

      <UserDialog
        open={open_edit_user_dialog}
        /* This dialog's title read "Add User". */
        title="Edit User"
        idPrefix="edit-user"
        values={values}
        onChange={handleChange}
        availableGroups={available_user_groups.data}
        selectedGroups={selected_user_groups}
        onToggleGroup={handleToggleGroup}
        onClose={() => setOpenEditUserDialog(false)}
        onSubmit={handleOnSubmit}
        actions={
          <>
            <DangerButton type="button" onClick={() => setOpenDeactivateDialog(true)}>
              Deactivate
            </DangerButton>
            <PrimaryButton type="submit">Update</PrimaryButton>
          </>
        }
      />

      <ConfirmDialog
        open={open_deactivate_dialog}
        title={fullName ? `Deactivate ${fullName}?` : "Deactivate this user?"}
        body="They immediately lose access to every product area their account is assigned to. Their record is kept — it stays linked to install history — and can be reactivated any time from the Inactive view."
        cancelLabel="Cancel"
        confirmLabel="Deactivate user"
        onCancel={() => setOpenDeactivateDialog(false)}
        onConfirm={handleDeactivate}
      />
    </>
  );
}
